import { View, Text, TouchableOpacity, StyleSheet, Platform, Image } from 'react-native';
import React, { useState, useCallback } from 'react';
import { ThemedText, ThemedTouchableOpacity } from '@app/theme/Components';
import { Theme, ThemedView, themeColorSet } from '@app/theme';
import FileUploadIcon from '@app/assets/svg/FileUploadIcon';
import { useTheme } from '@shopify/restyle';
import TrashIcon from '@app/assets/svg/TrashIcon';
import DP, {DocumentPickerResponse} from 'react-native-document-picker';
import { trackEvent } from '@app/utils/amplitude';

interface FileUploadProps {
    onFileSelect?: (file: any) => void;
    label?: string;
    accept?: string[];
    maxSize?: number; // in MB
    isUploading?: boolean;
    isFrontSide?: boolean;
}

const FileUpload = ({
    onFileSelect,
    label = 'Upload file',
    accept = ['*/*'],
    maxSize = 5,
    isUploading = false,
    isFrontSide = false,
}: FileUploadProps) => {
    const [selectedFile, setSelectedFile] = useState<any>(null);
    const [error, setError] = useState<string>('');
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    const {colors} = useTheme<Theme>()

    const handleFileSelect = useCallback(async (e: any) => {
        if(isFrontSide) {
            trackEvent('SUBMITBANKDOCS_DOCFRONTSIDE_UPLOAD') // need to test
        }
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (Platform.OS === 'web') {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = accept.join(',');
            
            input.onchange = (e: any) => {
                const file = e.target.files[0];
                if (file) {
                    const fileSizeInMB = file.size / (1024 * 1024);

                    if (fileSizeInMB > maxSize) {
                        setError(`File size ${fileSizeInMB.toFixed(1)}MB exceeds the limit of ${maxSize}MB. Please choose a smaller file.`);
                        return;
                    }

                    // Create preview URL for images
                    if (file.type.startsWith('image/')) {
                        const url = URL.createObjectURL(file);
                        setPreviewUrl(url);
                    } else if (file.type === 'application/pdf') {
                        setPreviewUrl('pdf');
                    }

                    setSelectedFile(file);
                    setError('');
                    onFileSelect?.(file);
                }
            };
            input.click();
        } else {
            const file = await DP.pick({type : [
                DP.types.pdf,
                DP.types.doc,
                DP.types.images,
            ],
        })
        const fileSizeInMB = (file[0]?.size ?? 0) / (1024 * 1024);

        if (fileSizeInMB > maxSize) {
            setError(`File size ${fileSizeInMB.toFixed(1)}MB exceeds the limit of ${maxSize}MB. Please choose a smaller file.`);
            return;
        } else {
            setSelectedFile(file[0])
            setPreviewUrl(file[0]?.uri || null);
            setError('');
            onFileSelect?.(file[0]);
        }
        }
    }, [accept, maxSize, onFileSelect]);

    const removeFile = useCallback(() => {
        if (previewUrl && previewUrl !== 'pdf') {
            URL.revokeObjectURL(previewUrl);
        }
        setSelectedFile(null);
        setPreviewUrl(null);
        setError('');
        onFileSelect?.(null);
        if(isFrontSide) {
            trackEvent('SUBMITBANKDOCS_DOCFRONTSIDE_DELETED') 
        }
    }, [onFileSelect, previewUrl]);

    const renderFilePreview = () => {
        if (!selectedFile) return null;

        if (previewUrl === 'pdf') {
            return (
                <View style={styles.pdfPreview}>
                    <FileUploadIcon color={colors['text-action-primary']} />
                    <ThemedText 
                        color="text-secondary" 
                        variant="b3r"
                        style={styles.fileName}
                        numberOfLines={1}
                    >
                        {selectedFile.name}
                    </ThemedText>
                </View>
            );
        }

        if (previewUrl) {
            return (
                <View style={styles.imagePreview}>
                    <Image 
                        source={{ uri: previewUrl }} 
                        style={styles.previewImage}
                        resizeMode="cover"
                    />
                    <ThemedText 
                        color="text-secondary" 
                        variant="b3r"
                        style={styles.fileName}
                        numberOfLines={1}
                    >
                        {selectedFile.name}
                    </ThemedText>
                </View>
            );
        }

        return null;
    };

    if(selectedFile) {
        return (
            <ThemedView overflow='hidden' borderWidth={1} borderColor='border-action' borderRadius='sm'>
                {renderFilePreview()}
                <ThemedView borderTopWidth={1} borderColor='border-action' paddingHorizontal='sm' paddingVertical='sm' bg='bg-surface-emphasis-primary' flexDirection='row' alignItems='center' justifyContent='space-between'>
                    <ThemedText mr='lg' variant='b4r' color='text-secondary'>
                        {selectedFile.name}
                    </ThemedText>
                    <ThemedTouchableOpacity onPress={isUploading ? undefined : removeFile}>
                        <TrashIcon size={16} color={colors['icon-error']} />
                    </ThemedTouchableOpacity>
                </ThemedView>
            </ThemedView>
        )
    }

    return (
        <ThemedView>
            <ThemedTouchableOpacity
                onPress={handleFileSelect}
                borderWidth={1}
                borderColor={error ? 'border-error' : 'border-action-hover'}
                borderStyle="dashed"
                borderRadius="sm"
                padding="md"
                alignItems="center"
                justifyContent="center"
                backgroundColor="bg-fill-emphasis-primary-1"
            >
                {selectedFile ? (
                    <ThemedView>
                        {/* {renderFilePreview()} */}
                        {/* <TouchableOpacity onPress={removeFile} style={styles.removeButton}>
                            <Text style={styles.removeText}>Remove</Text>
                        </TouchableOpacity> */}
                    </ThemedView>
                ) : (
                    <ThemedView flexDirection='row' alignItems='center'>
                        <ThemedText
                          mr='sm'
                            color={error ? 'text-error' : "text-action-primary"}
                            variant="b3b"
                            style={styles.uploadText}
                        >
                            {label}
                        </ThemedText>
                        <FileUploadIcon size={18} color={error ? colors['text-error'] : colors['text-action-primary']} />
                    </ThemedView>
                )}
            </ThemedTouchableOpacity>
            {error ? (
                <ThemedText
                    color="text-error"
                    variant="b4r"
                    style={styles.errorText}
                >
                    {error}
                </ThemedText>
            ) : (
                <ThemedText
                    color="text-secondary"
                    variant="b4r"
                    style={styles.uploadHint}
                >
                    Supported file format of max. size {maxSize} MB - jpeg, pdf, png
                </ThemedText>
            )}
        </ThemedView>
    );
};

const styles = StyleSheet.create({
    filePreview: {
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
    },
    fileName: {
        flex: 1,
        marginLeft: 8,
    },
    removeButton: {
        marginLeft: 8,
    },
    removeText: {
        color: '#EF4444',
        fontSize: 14,
    },
    uploadText: {
        // marginTop: 8,
    },
    uploadHint: {
        marginTop: 4,
    },
    errorText: {
        marginTop: 4,
    },
    imagePreview: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
    },
    previewImage: {
        width: '100%',
        height: 108,
        borderRadius: 4,
    },
    pdfPreview: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
    },
});

export default FileUpload;